import * as React from 'react';
import {
    Alert, Autocomplete, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControlLabel, Grid, IconButton, Link as MuiLink, ListSubheader, MenuItem, Stack, Switch, Table,
    TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { Link as RouterLink } from 'react-router-dom';
import {
    AI_LABELS, createAiUserBudget, deleteAiUserBudget, getAiEndUserBudgets, getAiUserBudgets, updateAiUserBudget,
} from '../../Api';
import {
    AiEndUserBudgetUsageDto, AiMetaDto, AiUpsertUserBudgetRequest, AiUserBudgetDto, AiUserBudgetWindowDto,
} from '../../models/dtos/AiDto';
import { ConfirmDialog } from '../betupFeed/BetupFeedShared';
import { BudgetProgress } from './AiHealthPanels';
import {
    AiActionError, AiErrorAlert, aiLoadErrorText, formatAiDate, formatAiUsd, parseOptionalNumber, textError, toAiActionError,
} from './AiShared';

const TIER_PATTERN = /^[a-z0-9_-]{1,64}$/;
const MAX_WINDOW_HOURS = 167;

/** A limit as typed by people: $0.25, $1.50, $0.125 (2–4 decimals). */
export function formatLimitUsd(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) return '—';
    return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

function shortWindowText(budget: Pick<AiUserBudgetDto, 'windowHours' | 'windowLimitUsd'>): string {
    return budget.windowHours != null && budget.windowLimitUsd != null
        ? `${formatLimitUsd(budget.windowLimitUsd)} / ${budget.windowHours} h`
        : '—';
}

export function targetText(agentCode: string | null, taskCode: string | null): string {
    return agentCode ? `agent ${agentCode}` : taskCode ? `task ${taskCode}` : 'All opted-in';
}

// The target select's value: '' = every opted-in agent and task, `agent:<code>` or `task:<code>`.
function targetValue(agentCode: string | null, taskCode: string | null): string {
    return agentCode ? `agent:${agentCode}` : taskCode ? `task:${taskCode}` : '';
}

function parseTarget(value: string): { agentCode: string | null; taskCode: string | null } {
    if (value.startsWith('agent:')) return { agentCode: value.slice('agent:'.length), taskCode: null };
    if (value.startsWith('task:')) return { agentCode: null, taskCode: value.slice('task:'.length) };
    return { agentCode: null, taskCode: null };
}

function CallerSelect({ value, callers, onChange, size, helperText }: {
    value: string;
    callers: string[];
    onChange: (value: string) => void;
    size?: 'small' | 'medium';
    helperText?: string;
}) {
    return (
        <TextField select fullWidth size={size} label="Caller" value={value} onChange={e => onChange(e.target.value)} helperText={helperText}
            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
            <MenuItem value=""><em>All callers</em></MenuItem>
            {callers.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            {value && !callers.includes(value) && <MenuItem value={value}>{value}</MenuItem>}
        </TextField>
    );
}

function TargetSelect({ value, agentCodes, taskCodes, onChange, size, helperText }: {
    value: string;
    agentCodes: string[];
    taskCodes: string[];
    onChange: (value: string) => void;
    size?: 'small' | 'medium';
    helperText?: string;
}) {
    // Keep a saved target selectable even when it is not in the loaded lists.
    const { agentCode, taskCode } = parseTarget(value);
    const agents = agentCode && !agentCodes.includes(agentCode) ? [...agentCodes, agentCode] : agentCodes;
    const tasks = taskCode && !taskCodes.includes(taskCode) ? [...taskCodes, taskCode] : taskCodes;
    return (
        <TextField select fullWidth size={size} label="Target" value={value} onChange={e => onChange(e.target.value)} helperText={helperText}
            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
            <MenuItem value=""><em>All opted-in agents and tasks</em></MenuItem>
            {agents.length > 0 && <ListSubheader>Agents</ListSubheader>}
            {agents.map(c => <MenuItem key={`agent:${c}`} value={`agent:${c}`}>{c}</MenuItem>)}
            {tasks.length > 0 && <ListSubheader>Tasks</ListSubheader>}
            {tasks.map(c => <MenuItem key={`task:${c}`} value={`task:${c}`}>{c}</MenuItem>)}
        </TextField>
    );
}

function TierInput({ value, tiers, onChange, size, helperText, error }: {
    value: string;
    tiers: string[];
    onChange: (value: string) => void;
    size?: 'small' | 'medium';
    helperText?: string;
    error?: boolean;
}) {
    return (
        <Autocomplete freeSolo options={tiers} inputValue={value} size={size}
            onInputChange={(_, v) => onChange((v ?? '').toLowerCase())}
            renderInput={params => <TextField {...params} label="Tier" placeholder="Default" helperText={helperText} error={error}
                InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />} />
    );
}

// ─── Create / edit dialog ────────────────────────────────────────────────────

interface UserBudgetForm {
    name: string;
    caller: string;
    target: string;
    tier: string;
    windowHours: string;
    windowLimitUsd: string;
    weekLimitUsd: string;
    enabled: boolean;
}

const EMPTY: UserBudgetForm = {
    name: '', caller: '', target: '', tier: '', windowHours: '', windowLimitUsd: '', weekLimitUsd: '', enabled: true,
};

function UserBudgetDialog({ open, budget, meta, taskCodes, agentCodes, onClose, onSaved }: {
    open: boolean;
    budget: AiUserBudgetDto | null;
    meta: AiMetaDto | null;
    taskCodes: string[];
    agentCodes: string[];
    onClose: () => void;
    onSaved: (message: string) => void;
}) {
    const [form, setForm] = React.useState<UserBudgetForm>(EMPTY);
    const [error, setError] = React.useState<AiActionError | null>(null);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        if (!open) return;
        setError(null);
        setForm(budget ? {
            name: budget.name,
            caller: budget.caller ?? '',
            target: targetValue(budget.agentCode, budget.taskCode),
            tier: budget.tier ?? '',
            windowHours: budget.windowHours != null ? String(budget.windowHours) : '',
            windowLimitUsd: budget.windowLimitUsd != null ? String(budget.windowLimitUsd) : '',
            weekLimitUsd: String(budget.weekLimitUsd),
            enabled: budget.enabled,
        } : EMPTY);
    }, [open, budget]);

    const patch = (p: Partial<UserBudgetForm>) => setForm(f => ({ ...f, ...p }));
    const tier = form.tier.trim();
    const tierInvalid = tier !== '' && !TIER_PATTERN.test(tier);

    const save = async () => {
        if (!form.name.trim()) return setError(textError('Name is required.'));
        const weekLimitUsd = parseOptionalNumber(form.weekLimitUsd);
        if (weekLimitUsd == null || weekLimitUsd <= 0) return setError(textError('Week limit must be a positive number.'));
        const windowHours = parseOptionalNumber(form.windowHours);
        const windowLimitUsd = parseOptionalNumber(form.windowLimitUsd);
        if (windowHours === undefined || windowLimitUsd === undefined) return setError(textError('Short window fields must be numbers.'));
        if ((windowHours == null) !== (windowLimitUsd == null)) {
            return setError(textError('Short window: set both hours and limit, or leave both empty.'));
        }
        if (windowHours != null && (!Number.isInteger(windowHours) || windowHours < 1 || windowHours > MAX_WINDOW_HOURS)) {
            return setError(textError(`Short window hours must be a whole number between 1 and ${MAX_WINDOW_HOURS}.`));
        }
        if (windowLimitUsd != null && windowLimitUsd <= 0) return setError(textError('Short window limit must be a positive number.'));
        if (tierInvalid) return setError(textError('Tier: lowercase letters, digits, "_" or "-" (at most 64), or empty for the default tier.'));
        const { agentCode, taskCode } = parseTarget(form.target);
        const request: AiUpsertUserBudgetRequest = {
            name: form.name.trim(),
            caller: form.caller || null,
            agentCode,
            taskCode,
            tier: tier || null,
            windowHours,
            windowLimitUsd,
            weekLimitUsd,
            enabled: form.enabled,
        };
        setSaving(true);
        const result = budget ? await updateAiUserBudget(budget.id, request) : await createAiUserBudget(request);
        setSaving(false);
        if (result.error != null) return setError(toAiActionError(result, 'Save failed'));
        onSaved(`Per-user budget ${request.name} saved.`);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{budget ? `Edit per-user budget ${budget.name}` : 'New per-user budget'}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={12}>
                        <TextField label="Name" fullWidth required value={form.name} onChange={e => patch({ name: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CallerSelect value={form.caller} callers={meta?.callers ?? []} onChange={caller => patch({ caller })}
                            helperText="The calling service (optional)" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TierInput value={form.tier} tiers={meta?.userBudgetTiers ?? []} onChange={v => patch({ tier: v })}
                            error={tierInvalid} helperText="Empty = the default tier" />
                    </Grid>
                    <Grid item xs={12}>
                        <TargetSelect value={form.target} agentCodes={agentCodes} taskCodes={taskCodes} onChange={target => patch({ target })}
                            helperText="Only agents and tasks with “Counts toward per-user budgets” on are counted; an agent counts its whole run." />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label="Short window (hours)" fullWidth value={form.windowHours} placeholder="none"
                            helperText={`1–${MAX_WINDOW_HOURS}, optional`}
                            onChange={e => patch({ windowHours: e.target.value.replace(/[^\d]/g, '') })} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label="Window limit (USD)" fullWidth value={form.windowLimitUsd} placeholder="none"
                            helperText="With the hours"
                            onChange={e => patch({ windowLimitUsd: e.target.value.replace(/[^\d.]/g, '') })} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label="Week limit (USD)" fullWidth required value={form.weekLimitUsd}
                            helperText="Rolling 7 days"
                            onChange={e => patch({ weekLimitUsd: e.target.value.replace(/[^\d.]/g, '') })} />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel control={<Switch checked={form.enabled} onChange={e => patch({ enabled: e.target.checked })} />} label="Enabled" />
                    </Grid>
                </Grid>
                <AiErrorAlert error={error} onClose={() => setError(null)} sx={{ mt: 2 }} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
            </DialogActions>
        </Dialog>
    );
}

// ─── End user lookup ─────────────────────────────────────────────────────────

function windowState(w: AiUserBudgetWindowDto): string {
    return w.blocked ? 'BLOCKING' : w.percent >= 80 ? 'WARNING' : 'OK';
}

function windowLabel(w: AiUserBudgetWindowDto): string {
    return w.window === 'WEEK' ? 'Week' : `${w.hours} h`;
}

function WindowRow({ w }: { w: AiUserBudgetWindowDto }) {
    return (
        <TableRow>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{windowLabel(w)}</TableCell>
            <TableCell sx={{ minWidth: 200 }}><BudgetProgress percent={w.percent} state={windowState(w)} /></TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatAiUsd(w.usedUsd)} / {formatAiUsd(w.limitUsd)}</TableCell>
            <TableCell>{w.blocked ? <Chip size="small" color="error" label="blocked" /> : <Chip size="small" variant="outlined" color="success" label="ok" />}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{w.resetsAt ? formatAiDate(w.resetsAt) : '—'}</TableCell>
        </TableRow>
    );
}

function EndUserBudgetLookup({ meta, taskCodes, agentCodes }: { meta: AiMetaDto | null; taskCodes: string[]; agentCodes: string[] }) {
    const [endUserId, setEndUserId] = React.useState('');
    const [caller, setCaller] = React.useState('');
    const [target, setTarget] = React.useState('');
    const [tier, setTier] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<AiActionError | null>(null);
    const [usage, setUsage] = React.useState<AiEndUserBudgetUsageDto | null>(null);

    const lookup = async () => {
        const id = Number(endUserId);
        if (!endUserId || !Number.isFinite(id) || id <= 0) return setError(textError('Enter an end user id.'));
        const tierText = tier.trim();
        if (tierText !== '' && !TIER_PATTERN.test(tierText)) return setError(textError('Tier: lowercase letters, digits, "_" or "-".'));
        setLoading(true);
        setError(null);
        const result = await getAiEndUserBudgets(id, { caller: caller || null, ...parseTarget(target), tier: tierText || null });
        setLoading(false);
        setUsage(result.data);
        if (result.error != null) setError(toAiActionError(result, 'Lookup failed'));
    };

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack gap={1.5}>
                    <Box>
                        <Typography variant="h6">End user lookup</Typography>
                        <Typography variant="body2" color="text.secondary">
                            What the per-user budgets allow one player right now: each window's use, when it resets, and whether calls are blocked.
                            Shown even for targets that are not opted in.
                        </Typography>
                    </Box>
                    <Grid container spacing={1.5} alignItems="flex-start">
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField size="small" fullWidth label="End user id" value={endUserId}
                                onChange={e => setEndUserId(e.target.value.replace(/\D/g, ''))}
                                onKeyDown={e => { if (e.key === 'Enter') lookup(); }} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <CallerSelect size="small" value={caller} callers={meta?.callers ?? []} onChange={setCaller} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TargetSelect size="small" value={target} agentCodes={agentCodes} taskCodes={taskCodes} onChange={setTarget} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TierInput size="small" value={tier} tiers={meta?.userBudgetTiers ?? []} onChange={setTier} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button variant="contained" fullWidth disabled={loading} onClick={lookup}
                                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}>
                                Look up
                            </Button>
                        </Grid>
                    </Grid>
                    <AiErrorAlert error={error} onClose={() => setError(null)} />
                    {usage && (
                        <Stack gap={1.5}>
                            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                                <Typography variant="subtitle1" fontWeight={600}>
                                    End user <MuiLink component={RouterLink} to={`/users/${usage.endUserId}`} underline="hover">#{usage.endUserId}</MuiLink>
                                </Typography>
                                <Chip size="small" variant="outlined" label={`tier ${usage.tier ?? 'default'}`} />
                                {usage.blocked
                                    ? <Chip size="small" color="error" label="blocked" />
                                    : <Chip size="small" color="success" variant="outlined" label="not blocked" />}
                                {usage.primary && (
                                    <Typography variant="body2" color="text.secondary">
                                        Closest to its limit: {windowLabel(usage.primary)} window, {formatAiUsd(usage.primary.usedUsd)} of {formatAiUsd(usage.primary.limitUsd)} ({usage.primary.percent.toFixed(1)}%)
                                    </Typography>
                                )}
                            </Stack>
                            {usage.budgets.length === 0 && (
                                <Typography variant="body2" color="text.secondary">No per-user budget applies to this end user and target.</Typography>
                            )}
                            {usage.budgets.map(b => (
                                <Box key={b.budgetId}>
                                    <Stack direction="row" gap={1} alignItems="baseline" flexWrap="wrap">
                                        <Typography variant="body2" fontWeight={600}>{b.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {b.caller ?? 'All callers'} · {targetText(b.agentCode, b.taskCode)} · tier {b.tier ?? 'default'}
                                        </Typography>
                                    </Stack>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Window</TableCell>
                                                <TableCell>Used</TableCell>
                                                <TableCell>Spent / limit</TableCell>
                                                <TableCell>State</TableCell>
                                                <TableCell>Resets at</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {b.windows.map(w => <WindowRow key={`${w.window}-${w.hours}`} w={w} />)}
                                        </TableBody>
                                    </Table>
                                </Box>
                            ))}
                        </Stack>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}

// ─── Tab ─────────────────────────────────────────────────────────────────────

/** AI › Budgets › Per user: per-user budgets CRUD and an end user lookup. */
export default function AiUserBudgetsTab({ meta, taskCodes, agentCodes }: { meta: AiMetaDto | null; taskCodes: string[]; agentCodes: string[] }) {
    const [budgets, setBudgets] = React.useState<AiUserBudgetDto[]>([]);
    const [loadError, setLoadError] = React.useState<string | null>(null);
    const [error, setError] = React.useState<AiActionError | null>(null);
    const [notice, setNotice] = React.useState<string | null>(null);
    const [dialog, setDialog] = React.useState<{ open: boolean; budget: AiUserBudgetDto | null }>({ open: false, budget: null });
    const [pendingDelete, setPendingDelete] = React.useState<AiUserBudgetDto | null>(null);

    const load = React.useCallback(async () => {
        const b = await getAiUserBudgets();
        setBudgets(b ?? []);
        setLoadError(b == null ? aiLoadErrorText(AI_LABELS.userBudgets, 'Failed to load per-user budgets.') : null);
    }, []);
    React.useEffect(() => { load(); }, [load]);

    const remove = async () => {
        if (pendingDelete == null) return;
        const target = pendingDelete;
        setPendingDelete(null);
        const result = await deleteAiUserBudget(target.id);
        if (result.error != null) {
            setError(toAiActionError(result, 'Delete failed'));
            return;
        }
        setNotice(`Per-user budget ${target.name} deleted.`);
        load();
    };

    return (
        <Stack gap={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 900 }}>
                    Rolling caps on what the hub spends for one player (end user). Only agents and tasks with “Counts toward per-user budgets”
                    on are counted and blocked (429 USER_BUDGET_EXCEEDED); calls without an end user are never affected. A tier's budget
                    replaces the default one of the same caller and target.
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ open: true, budget: null })}>New per-user budget</Button>
            </Stack>
            {loadError && <Alert severity="error">{loadError}</Alert>}
            <AiErrorAlert error={error} onClose={() => setError(null)} />
            {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
            <Card variant="outlined">
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Budget</TableCell>
                                <TableCell>Caller</TableCell>
                                <TableCell>Target</TableCell>
                                <TableCell>Tier</TableCell>
                                <TableCell>Short window</TableCell>
                                <TableCell>Week limit</TableCell>
                                <TableCell>Enabled</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {budgets.length === 0 && <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary">No per-user budgets.</Typography></TableCell></TableRow>}
                            {budgets.map(b => (
                                <TableRow key={b.id} sx={{ opacity: b.enabled ? 1 : 0.6 }}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>{b.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">updated {formatAiDate(b.updatedAt)}</Typography>
                                    </TableCell>
                                    <TableCell>{b.caller ?? 'All callers'}</TableCell>
                                    <TableCell>{targetText(b.agentCode, b.taskCode)}</TableCell>
                                    <TableCell>{b.tier ?? 'Default'}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{shortWindowText(b)}</TableCell>
                                    <TableCell>{formatLimitUsd(b.weekLimitUsd)}</TableCell>
                                    <TableCell>
                                        {b.enabled
                                            ? <Chip size="small" color="success" variant="outlined" label="enabled" />
                                            : <Chip size="small" variant="outlined" label="disabled" />}
                                    </TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Tooltip title="Edit"><IconButton size="small" onClick={() => setDialog({ open: true, budget: b })}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setPendingDelete(b)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
            <EndUserBudgetLookup meta={meta} taskCodes={taskCodes} agentCodes={agentCodes} />
            <UserBudgetDialog open={dialog.open} budget={dialog.budget} meta={meta} taskCodes={taskCodes} agentCodes={agentCodes}
                onClose={() => setDialog({ open: false, budget: null })}
                onSaved={message => { setDialog({ open: false, budget: null }); setNotice(message); setError(null); load(); }} />
            <ConfirmDialog
                open={pendingDelete != null}
                title="Delete per-user budget?"
                text={<>Per-user budget <b>{pendingDelete?.name}</b> will be removed — players it blocked can call again immediately (unless another budget still applies).</>}
                confirmLabel="Delete"
                confirmColor="error"
                onClose={() => setPendingDelete(null)}
                onConfirm={remove}
            />
        </Stack>
    );
}
